import { useTranslation } from 'react-i18next';
import {
  useDeleteLanguage,
  useGetLanguages,
} from '../../../services/redux/api/languagesAPI';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { ILanguage } from '../../../types/interfaces/language';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import { FormikProps, useFormik } from 'formik';

export function useLanguagesComponent(classes) {
  const { t } = useTranslation();
  const { data: languages } = useGetLanguages();
  const [itemForDelete, setItemForDelete] = useState<ILanguage>(null);
  const [itemForEditing, setItemForEditing] = useState<ILanguage>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteLanguage] = useDeleteLanguage();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      active: allOption,
    },
    onSubmit: () => {},
  });

  const filteredValues = useMemo(
    () =>
      languages?.filter((property: ILanguage) => {
        const activeFilterId = filterFormik.values.active?.id ?? 'all';
        const filterValue = filter.toLowerCase();
        const includesFilter =
          String(property.name ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(property.key ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(property.title ?? '')
            .toLowerCase()
            .includes(filterValue);

        if (!includesFilter) return false;

        if (activeFilterId !== 'all') {
          const isActive = !!property.active;
          if (
            (activeFilterId === true && !isActive) ||
            (activeFilterId === false && isActive)
          ) {
            return false;
          }
        }

        return true;
      }),
    [languages, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.languages?.canEdit ?? false,
      canDelete: access?.languages?.canDelete ?? false,
    },
    (property: ILanguage) => {
      setItemForEditing(property);
      setShowEditModal(true);
    },
    (property: ILanguage) => {
      setItemForDelete(property);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.EXERCISE_PROPERTY_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteLanguage({ id: itemForDelete.id });
  }

  const filterActiveOptions = [
    allOption,
    { id: true, name: t('common.active') },
    { id: false, name: t('common.notActive') },
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterFormik,
    filterActiveOptions,
  };
}
